import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  TextInput,
  Animated,
  Platform,
} from 'react-native';
import { auth, firestore } from '../config/firebase';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { translations, Language } from '../config/translations';

const LoginScreen = ({ navigation }: any) => {
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState<Language>('nl');
  const [manualCode, setManualCode] = useState('');

  useEffect(() => {
    loadLanguage();
  }, []);

  const loadLanguage = async () => {
    try {
      const savedLanguage = await AsyncStorage.getItem('selectedLanguage');
      setLanguage((savedLanguage as Language) || 'nl');
    } catch (error) {
      console.error('Error loading language:', error);
      setLanguage('nl');
    }
  };
  
  const t = translations[language] || translations.nl;

  const createUserDocument = async (userId: string, groupId: string) => {
    try {
      const userRef = doc(firestore, 'users', userId);
      await setDoc(userRef, {
        groupId: groupId,
        role: 'user',
        createdAt: new Date().toISOString(),
      });
      return true;
    } catch (error) {
      console.error('Error creating user document:', error);
      return false;
    }
  };

  const handleScanPress = () => {
    if (Platform.OS === 'web') {
      navigation.navigate('QrScanner');
    } else {
      Alert.alert(
        t.login.cameraPermission.title || "Camera Toegang", 
        "Native QR scanning is currently not implemented."
      );
    }
  };

  const handleManualSubmit = async () => {
    if (!manualCode.trim()) {
      Alert.alert('Error', t.login.error.emptyCode || 'Voer een code in');
      return;
    }

    try {
      setLoading(true);

      console.log('Authenticating anonymously for manual login...');
      const userCredential = await signInAnonymously(auth);
      if (!userCredential?.user?.uid) {
        throw new Error('authentication_failed');
      }
      const userId = userCredential.user.uid;
      console.log('Anonymous authentication successful, UID:', userId);

      console.log('Verifying manual code in Firestore:', manualCode.trim());
      const groupsRef = collection(firestore, 'groups');
      const q = query(groupsRef, where('qrCode', '==', manualCode.trim()));
      const groupSnapshot = await getDocs(q);

      if (groupSnapshot.empty) {
         console.error('Invalid manual code: No matching group found.');
        throw new Error('invalid_code');
      }

      const groupDoc = groupSnapshot.docs[0];
      const groupId = groupDoc.id;
      const groupName = groupDoc.data().name || 'Unknown Group';
      console.log(`Group found: ID=${groupId}, Name=${groupName}`);

      const userCreated = await createUserDocument(userId, groupId);
      if (!userCreated) {
        throw new Error('user_creation_failed');
      }

      await AsyncStorage.setItem('currentGroupId', groupId);
      await AsyncStorage.setItem('currentQrCode', manualCode.trim());
      await AsyncStorage.setItem('isLoggedIn', 'true');
      
      // --- Clean up pending keys --- 
      await AsyncStorage.removeItem('pendingGroupCode');
      await AsyncStorage.removeItem('pendingGroupName');
      console.log('Cleared pending keys after successful manual login.');
      // --- End Clean up ---

      console.log('Manual login successful, navigating to Home...');
      navigation.replace('Home');
    } catch (error: any) {
      console.error('Manual code error:', error);
      let errorMessage = t.login.error.generalError || 'Er is een fout opgetreden';
       if (error.message === 'invalid_code') {
         errorMessage = t.login.error.invalidManualCode || 'De ingevoerde code is ongeldig';
       } else if (error.message === 'authentication_failed') {
         errorMessage = 'Authenticatie mislukt. Probeer het opnieuw.';
       } else if (error.message === 'user_creation_failed') {
         errorMessage = 'Kon gebruikersprofiel niet aanmaken. Probeer het opnieuw.';
       }
       Alert.alert('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t.login.welcome}</Text>
      <Text style={styles.instructions}>{t.login.instructions}</Text>

      <TouchableOpacity
        style={[styles.button, loading && styles.disabledButton]}
        onPress={handleScanPress}
        disabled={loading}
      >
        {loading ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <Text style={styles.buttonText}>{t.login.scanQRCode}</Text>
        )}
      </TouchableOpacity>

      <Text style={styles.orText}>{t.login.or}</Text>

      <TextInput
        style={styles.input}
        placeholder={t.login.manualCodePlaceholder}
        value={manualCode}
        onChangeText={setManualCode}
        autoCapitalize="none"
        editable={!loading}
      />

      <TouchableOpacity
        style={[styles.button, styles.manualSubmitButton, loading && styles.disabledButton]}
        onPress={handleManualSubmit}
        disabled={loading}
      >
        {loading ? (
           <ActivityIndicator color="#FFFFFF" />
        ) : (
           <Text style={styles.buttonText}>{t.login.submitCode}</Text>
        )}
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#f5f5f5',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#333',
  },
  instructions: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 30,
    color: '#666',
  },
  button: {
    backgroundColor: '#005575',
    paddingVertical: 15,
    paddingHorizontal: 30,
    borderRadius: 8,
    width: '80%',
    alignItems: 'center',
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  disabledButton: {
    opacity: 0.7,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  orText: {
    fontSize: 16,
    color: '#666',
    marginVertical: 20,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    padding: 15,
    width: '80%',
    marginBottom: 20,
    fontSize: 16,
    color: '#333',
  },
  manualSubmitButton: {
    // Optional: Slightly different style for the manual submit button
  },
});

export default LoginScreen; 